import { useMemo } from "react";

import toast from "react-hot-toast";

import { FilmIcon, Trash2Icon } from "@lucide/icons";

import { FileChooserDropzoneTrigger } from "simplydo/react/components/Choosers/FileChooser/FileChooserDropzoneTrigger";
import { allowedVideoTypesList } from "simplydo/react/components/Choosers/FileChooser/uploadUtils";
import { VideoPlayer } from "simplydo/react/components/VideoPlayer";
import { Button } from "simplydo/react/ui/button";
import {
  Card,
  CardContent,
  CardGroup,
  CardHeader,
} from "simplydo/react/ui/card";
import { Icon } from "simplydo/react/ui/icon";
import { Segment } from "simplydo/react/ui/segment";

import { VideoChooser } from "components/lib/Choosers";

import { FieldProps } from "./FieldProps";

type VideoComponentProps = {
  ideaId: string;
  video: string;
  onRemove?: () => void;
};

const VideoComponent = ({ video, onRemove }: VideoComponentProps) => {
  const memoisedPlayer = useMemo(
    () => <VideoPlayer src={video} style={{ marginTop: "20px" }} />,
    [video],
  );

  return (
    <CardGroup>
      <Card style={{ width: "80%" }}>
        <CardContent style={{ color: "#808080" }}>
          <Icon icon={FilmIcon} />
          Uploaded video
        </CardContent>
        <CardContent textAlign="center">
          <CardHeader style={{ overflowWrap: "break-word" }}>
            {memoisedPlayer}
          </CardHeader>
        </CardContent>
        {onRemove ? (
          <CardContent extra>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 10,
              }}
            >
              <Button
                size="small"
                onClick={onRemove}
                style={{ marginLeft: 0, marginRight: 0 }}
                variant="outline"
              >
                <Icon icon={Trash2Icon} />
              </Button>
            </div>
          </CardContent>
        ) : null}
      </Card>
    </CardGroup>
  );
};

const Video = ({
  field,
  ideaId,
  fieldValue = "",
  updateIdea,
  isEditing,
}: FieldProps) => {
  const onComplete = (_v: unknown, _name: unknown, videoSrc: string) => {
    // The video player doesn't respond well to updates, therefore we reset the video to null first
    updateIdea(null, ["templated", field.id], true);
    updateIdea(videoSrc, ["templated", field.id]);
  };

  if (!isEditing) {
    if (fieldValue) {
      return <VideoComponent video={fieldValue as string} ideaId={ideaId} />;
    }
    return null;
  }

  if (fieldValue) {
    return (
      <Segment placeholder>
        <VideoComponent
          video={fieldValue as string}
          ideaId={ideaId}
          onRemove={() => updateIdea(null, ["templated", field.id])}
        />
      </Segment>
    );
  }

  return (
    <VideoChooser
      trigger={
        <FileChooserDropzoneTrigger
          forType="idea"
          forId={ideaId}
          accept={allowedVideoTypesList.join(",")}
          onComplete={onComplete}
          dropzoneProps={{
            title: "Upload a video",
            DropZoneComponent: Segment,
            defaultIcon: FilmIcon,
          }}
        />
      }
      forType="idea"
      forId={ideaId}
      onComplete={onComplete}
      onError={toast.error}
    />
  );
};

export default Video;
